import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '@shared/api/api.config';

const API_URL = API_CONFIG.API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Интерцептор для обновления токена
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const response = await axios.post(`${API_URL}/auth/refresh/`, {
          refresh: refreshToken
        });

        localStorage.setItem('accessToken', response.data.access);
        originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        
        // Предотвращаем бесконечный цикл перезагрузки
        if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token && !isAuthenticated && !isFetching) {
      fetchCurrentUser();
    } else if (!token) {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    if (isFetching) return;
    setIsFetching(true);
    try {
      const response = await api.get('/auth/me/');
      setUser(response.data);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      // Ошибки обрабатываются интерцептором
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsFetching(false);
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login/', {
        username,
        password
      });

      if (response.data.access && response.data.refresh) {
        const { access, refresh, user: userData } = response.data;

        localStorage.setItem('accessToken', access);
        localStorage.setItem('refreshToken', refresh);
        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
        }

        setUser(userData || null);
        setIsAuthenticated(true);

        return { success: true };
      }

      return {
        success: false,
        error: 'Неожиданный формат ответа от сервера'
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка подключения к серверу'
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await api.post('/auth/register/', {
        username,
        email,
        password
      });

      if (response.data.access && response.data.refresh) {
        const { access, refresh, user: userData } = response.data;

        localStorage.setItem('accessToken', access);
        localStorage.setItem('refreshToken', refresh);
        if (userData) {
          localStorage.setItem('user', JSON.stringify(userData));
        }

        setUser(userData || null);
        setIsAuthenticated(true);

        return { success: true };
      }

      return {
        success: false,
        error: 'Неожиданный формат ответа от сервера'
      };
    } catch (error) {
      let errorMessage = 'Ошибка регистрации';

      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorList = [];

        if (errors.username) errorList.push(`Имя пользователя: ${errors.username}`);
        if (errors.email) errorList.push(`Email: ${errors.email}`);
        if (errors.password) errorList.push(`Пароль: ${errors.password}`);

        if (errorList.length > 0) {
          errorMessage = errorList.join('\n');
        } else {
          errorMessage = JSON.stringify(errors);
        }
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = '/';
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.patch('/users/profile/update/', profileData);
      const updatedUser = {
        ...response.data,
        avatar: response.data.avatar || user?.avatar
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка обновления'
      };
    }
  };

  const uploadAvatar = async (file) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await api.post('/users/profile/avatar/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.user) {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      } else {
        const updatedUser = { ...user, avatar: response.data.avatar_url };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Ошибка загрузки аватара'
      };
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      login,
      register,
      logout,
      updateUser,
      updateProfile,
      uploadAvatar,
      api
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};